import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlanActionButtons from "./PlanActionButton";

// Safe JSON parsing that always returns an array
const parseJSON = (str) => {
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const WeeklyPlanAccordion = ({ weeklyPlan }) => {
  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {weeklyPlan.map((plan, index) => {
        const labours = plan.labours_data ? parseJSON(plan.labours_data) : [];
        const materials = plan.materials ? parseJSON(plan.materials) : [];

        return (
          <Accordion key={plan.id} defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                minHeight: "48px",
              }}
            >
              {/* Control the width of the content */}
              <Box sx={{ width: "50%" }}>
                <Typography variant="h6" sx={{ color: "white" }}>
                  Plan: {index + 1}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography>
                      <strong>Project Type:</strong> {plan.project_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography>
                      <strong>Employee Name:</strong> {plan.employee_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography>
                      <strong>From Date:</strong>{" "}
                      {plan.startDate
                        ? new Date(plan.startDate).toLocaleDateString("en-GB")
                        : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography>
                      <strong>To Date:</strong>{" "}
                      {plan.endDate
                        ? new Date(plan.endDate).toLocaleDateString("en-GB")
                        : ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography>
                      <strong>Work Type:</strong> {plan.work_type_name}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Labour Section */}
                {labours.length > 0 && (
                  <Box mt={2}>
                    <Typography
                      variant="h6"
                      sx={{ color: "green", fontWeight: "bold", mb: 1 }}
                    >
                      Labour Planning
                    </Typography>

                    <Grid container spacing={1}>
                      {labours.map((group, idx) => {
                        const labourNames = group.labours
                          .map((labour) => labour.name)
                          .join(", ");

                        return (
                          <React.Fragment key={idx}>
                            <Grid item xs={12} md={3}>
                              <Typography fontWeight="bold">
                                {group.labour_type_name} :
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={9}>
                              <Typography>{labourNames}</Typography>
                            </Grid>
                          </React.Fragment>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

                {/* Material Section */}
                {materials.length > 0 && (
                  <Box mt={2}>
                    <Grid container spacing={1} alignItems="flex-start">
                      {/* Label */}
                      <Grid item xs={12} md={3}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          gutterBottom
                          sx={{ whiteSpace: "nowrap" }} // prevent label from wrapping
                        >
                          Material Details:
                        </Typography>
                      </Grid>

                      {/* Table */}
                      <Grid item xs={12} md={8}>
                        <Box
                          component="table"
                          sx={{
                            width: "100%", // Make table take full width of container
                            maxWidth: 600, // Optional max width (can remove if you want)
                            borderCollapse: "collapse",
                            fontSize: "0.875rem",
                          }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  border: "1px solid #ccc",
                                  padding: "4px 8px",
                                  textAlign: "center",
                                }}
                              >
                                Name
                              </th>
                              <th
                                style={{
                                  border: "1px solid #ccc",
                                  padding: "4px 8px",
                                  textAlign: "center",
                                }}
                              >
                                Quantity
                              </th>
                              <th
                                style={{
                                  border: "1px solid #ccc",
                                  padding: "4px 8px",
                                  textAlign: "center",
                                }}
                              >
                                Unit
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {materials.map((material, i) => (
                              <tr key={i}>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "4px 8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {material.name}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "4px 8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {material.qty}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "4px 8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {material.unit_name}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Grid container spacing={1} alignItems="flex-start" mt={2}>
                  <Grid item xs={12} md={3}>
                    <Typography fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                      Description:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Typography>{plan.description || "-"}</Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={1} alignItems="flex-start" mt={2}>
                  <Grid item xs={12} md={3}>
                    <Typography fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                      Remarks by QS:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Typography>{plan.qs_remarks || "-"}</Typography>
                  </Grid>
                </Grid>
                <PlanActionButtons
                  planId={plan.id}
                  qsStatus={plan.qs_status}
                  pmStatus={plan.pm_status}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default WeeklyPlanAccordion;
